import find from 'lodash/find';
import React, { FC, useCallback } from 'react';
import styled from 'styled-components';

import { baseChannelOptions } from './MessageChannelCondition';
import ConditionRow from './ConditionRow';
import Input, { InputType } from 'src/presentation/components/forms/Input';
import {
  StyledDropdown,
  StyledEntityRow,
  StyledInformationIcon,
  StyledRowItem,
  StyledTooltip,
  StyledTypeDescription,
} from 'src/presentation/components/pages/rules/shared/StyledComponents';
import {
  EmptyInteractionType,
  InteractionType,
  WaitTimeCondition as WaitTimeConditionType,
} from 'src/actions/rules/condition';
import useActiveCustomChannels from 'src/presentation/components/pages/rules/hooks/useActiveCustomChannels';

export const CHANNEL_DROPDOWN_ANY_VALUE = 'ANY';
const anyChannelOption = [{ label: 'Any', value: CHANNEL_DROPDOWN_ANY_VALUE }];

type WaitTimeConditionProps = {
  condition: WaitTimeConditionType;
  index: number;
  isReadOnly?: boolean;
  onDelete: Function;
  onUpdate: Function;
};

const WaitTimeCondition: FC<WaitTimeConditionProps> = ({ condition, index, isReadOnly, onDelete, onUpdate }) => {
  const onMinutesChange = useCallback(
    evt => {
      const minutes = parseInt(evt.target.value);
      if (isNaN(minutes) || minutes <= 0) return;

      condition.setMinutes(minutes);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const onChannelChange = useCallback(
    val => {
      condition.setChannel(val === CHANNEL_DROPDOWN_ANY_VALUE ? EmptyInteractionType.Any : val);
      onUpdate({ condition, index });
    },
    [condition, index, onUpdate]
  );

  const { loadingState: customChannelLoadingState, values: customChannels } = useActiveCustomChannels();

  if (customChannelLoadingState === 'loading') return null;

  const messagingChannelOptions = customChannels
    .map(c => ({ label: c.name, value: `${InteractionType.CustomChannel}:${c.id}` }))
    .concat(baseChannelOptions);

  messagingChannelOptions.sort((a, b) => a.label.toLowerCase().localeCompare(b.label.toLowerCase()));

  const channelOptions = anyChannelOption.concat(messagingChannelOptions);

  const isPlural = condition.minutes !== 1;

  return (
    <>
      <ConditionRow index={index} isReadOnly={isReadOnly} onDelete={onDelete} text="Wait Time is greater than">
        {isReadOnly ? renderReadOnlyMinutes() : renderMinutes()}
      </ConditionRow>
      <SpacedWaitTimeEntityRow>
        <StyledRowItem>And</StyledRowItem>
        <StyledTypeDescription>Channel is</StyledTypeDescription>
        {isReadOnly ? (
          renderChannelLabel()
        ) : (
          <StyledDropdown
            data-aid="waitTimeCondition-dropdown"
            maxRows={5}
            onSelect={onChannelChange}
            options={channelOptions}
            placeholder="Select channel"
            searchable
            value={condition.channel === EmptyInteractionType.Any ? CHANNEL_DROPDOWN_ANY_VALUE : condition.channel}
          />
        )}
      </SpacedWaitTimeEntityRow>
    </>
  );

  function renderMinutes() {
    return (
      <StyledSection>
        <StyledMinutes>
          <StyledInput
            attr="minutes"
            data-aid={'waitTimeCondition-minutesInput'}
            onChange={onMinutesChange}
            type={InputType.number}
            value={condition.minutes.toString()}
          />
          <div>
            <StyledTooltip message="Value must be a positive number">
              <StyledInformationIcon />
            </StyledTooltip>
          </div>
          <StyledMinuteLabel>{`Minute${isPlural ? 's' : ''}`}</StyledMinuteLabel>
        </StyledMinutes>
      </StyledSection>
    );
  }

  function renderReadOnlyMinutes() {
    return (
      <>
        <StyledMinuteValue data-aid="readOnlyMinutesValue">{condition.minutes}</StyledMinuteValue>
        <StyledMinuteLabel data-aid="readOnlyMinutesLabel">{`Minute${isPlural ? 's' : ''}`}</StyledMinuteLabel>
      </>
    );
  }

  function renderChannelLabel() {
    const value = condition.channel === EmptyInteractionType.Any ? CHANNEL_DROPDOWN_ANY_VALUE : condition.channel;
    const channelOption = find(channelOptions, { value });
    return <span data-aid="readOnlyChannelValue">{channelOption ? channelOption.label : ''}</span>;
  }
};

const SpacedWaitTimeEntityRow = styled(StyledEntityRow)`
  margin-top: 8px;
`;

const StyledInput = styled(Input)`
  flex: 0;
  margin-left: 12px;
  margin-right: 12px;
  width: 50px;
`;

const StyledMinuteLabel = styled.div`
  display: flex;
  flex: 1;
`;

const StyledMinuteValue = styled.span`
  margin-right: 4px;
`;

const StyledMinutes = styled.div`
  align-items: center;
  display: flex;
  flex: 0;
`;

const StyledSection = styled.div`
  display: flex;
  flex: 1 0 auto;
`;

export default WaitTimeCondition;
